import { Box, TableCell, TableRow } from "@mui/material";
import { DotLegalEmptyState, DotLegalHeader, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import { useUrlProvider } from "../../../useUrlProvider";
import IndicatorRiskChip from "../../../risk/IndicatorRiskChip";
import DashboardPrettyScrollbar from "../../DashboardPrettyScrollbar";
import WhiteDashboardBox from "../../WhiteDashboardBox";
import { RiskDashboardTopRisk } from "../RiskDashboardWidget.types";
import NoRisky from "../../riskWidget/riskWidgetList/no-risky-entities.svg?react";

// The heading names the cap, so it has to stay in step with RiskDashboardQueries.TopRisksCount, which enforces it.
const topRisksLimit = 10;

export interface IRiskDashboardWidgetTopRisksProps {
    rows: Array<RiskDashboardTopRisk>;
    isLoading: boolean;
    isPdf?: boolean;
}

function RiskDashboardWidgetTopRisks(props: IRiskDashboardWidgetTopRisksProps) {
    const { translateString } = useTranslation();
    const { getRiskAssessmentsOverviewUrl } = useUrlProvider();

    // The entity column takes all the slack so the risk chip stays pinned to the right edge however short the names.
    // The area column is what tells two rows for the same entity apart.
    // Every column is notSortable: the ten rows are a fixed, server-ranked slice, and disableFilterAndSortAndSlicing
    // alone would leave the headers clickable while reordering nothing. Sorting them client-side is not merely
    // pointless but wrong for the risk column, since raw levels from areas on different scales are not comparable.
    const headers: Array<ITableHeader<RiskDashboardTopRisk>> = [
        { property: "name", text: translateString("entity"), width: "100%", notSortable: true },
        { property: "riskAreaName", text: translateString("riskArea"), notSortable: true },
        { property: "riskLevel", text: translateString("risk"), align: "right", notSortable: true },
    ];

    // Only observable once the box has stopped showing its loading skeleton.
    const isEmpty = !props.isLoading && props.rows.length === 0;

    const getTable = () => {
        const table = (
            <DotLegalTable
                type={"plain"}
                headers={headers}
                // Required by the table, and the risk column is genuinely what the query ranked by, so the header
                // shows a disabled indicator on it rather than none at all.
                defaultOrderBy={"riskLevel"}
                defaultOrder={"desc"}
                disableFilterAndSortAndSlicing
                clickableRows={false}
                data={props.rows}
                hidePagination
                marginTop={0}
                isPdf={props.isPdf}
                renderRow={(row) => (
                    // An entity assessed in several areas takes one row per area, so the key has to include the area.
                    <TableRow key={`${row.riskAreaId}-${row.type}-${row.entityId}`}>
                        <TableCell>
                            <EntityTypeIconWithName
                                type={row.type}
                                name={row.name}
                                linkTo={props.isPdf ? undefined : getRiskAssessmentsOverviewUrl(row.riskAreaId, row.type, row.entityId)}
                            />
                        </TableCell>
                        <TableCell>{row.riskAreaName}</TableCell>
                        <TableCell align="right">
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                {/* Indicators ride per row: each area configures its own, and a level with no
                                    indicator in the row's own area would render no chip at all. */}
                                <IndicatorRiskChip level={row.riskLevel} label={row.riskLabel} riskLevelIndicators={row.riskLevelIndicators} />
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
            />
        );

        return props.isPdf ? table : <DashboardPrettyScrollbar>{table}</DashboardPrettyScrollbar>;
    };

    return (
        <Box sx={{ flex: 1, mt: 2, display: "flex", flexDirection: "column" }}>
            <DotLegalHeader marginBottom={1} headerStyle={"medium"}>
                {translateString("dashboardTopHighestRisks", { limit: topRisksLimit })}
            </DotLegalHeader>
            <WhiteDashboardBox isLoading={props.isLoading} fullHeight paddingX={isEmpty ? undefined : 0}>
                {isEmpty ? (
                    <DotLegalEmptyState textColor={"darkBlue"} noFixedHeight noPadding icon={<NoRisky />} text={translateString("dashboardNoAssessedRisks")} />
                ) : (
                    getTable()
                )}
            </WhiteDashboardBox>
        </Box>
    );
}

export default RiskDashboardWidgetTopRisks;
